/**
 * Static methods for BaseModal
 */


import Modal                        from './Modal.class.js';
import {DEFAULT_CONFIG, modalStack} from './constants.js';

// Registered plugins store
const registeredPlugins = {};

// Keep references to active modals
let activeModal    = null;
const activeModals = {};
let config         = {...DEFAULT_CONFIG};

/**
 * Generate trigger map
 */
function generateTriggerMap(triggers, triggerAttr) {
    const triggerMap = [];

    triggers.forEach((trigger) => {
        const targetModal = trigger.attributes[triggerAttr].value;
        if (triggerMap[targetModal] === undefined) triggerMap[targetModal] = [];
        triggerMap[targetModal].push(trigger);
    });

    return triggerMap;
}

/**
 * Show modal by ID
 * @param {string} targetModal - Modal element ID
 * @param {Object} options - Modal options override
 * @returns {Promise|undefined} Show promise
 */
export function show(targetModal, options = {}) {
    if (!targetModal) {
        console.error('BaseModal.show: targetModal id is required');
        return Promise.reject(new Error('targetModal id is required'));
    }

    const trimmedId    = `${targetModal}`.trim();
    const modalElement = document.getElementById(trimmedId);

    if (!modalElement) {
        console.error(`BaseModal.show: modal with id "${trimmedId}" not found in DOM`);
        return Promise.reject(new Error(`Modal with id "${trimmedId}" not found`));
    }

    // Check if modal is already open or in animation
    const existingModal = activeModals[trimmedId];
    if (existingModal) {
        if (existingModal.isOpening) {
            if (config.debugMode) {
                console.log(`BaseModal.show: modal "${trimmedId}" is already opening`);
            }
            return Promise.resolve(existingModal);
        }
        if (existingModal.isClosing) {
            if (config.debugMode) {
                console.log(`BaseModal.show: modal "${trimmedId}" is closing, waiting...`);
            }
            // Return promise that waits for close then opens
            return new Promise((resolve) => {
                const checkClosed = () => {
                    if (!existingModal.isClosing && !modalElement.classList.contains(config.openClass || 'is-open')) {
                        activeModal = existingModal;
                        resolve(existingModal.showModal());
                    } else {
                        setTimeout(checkClosed, 50);
                    }
                };
                checkClosed();
            });
        }
        if (modalElement.classList.contains(config.openClass || 'is-open')) {
            if (config.debugMode) {
                console.log(`BaseModal.show: modal "${trimmedId}" is already open`);
            }
            return Promise.resolve(existingModal);
        }
    }

    const modalOptions = {
        ...config,
        targetModal:trimmedId, ...options
    };

    try {
        if (!activeModals[trimmedId]) {
            activeModals[trimmedId] = new Modal(modalOptions);
        }

        activeModal = activeModals[trimmedId];
        return activeModal.showModal();
    } catch (error) {
        console.error('BaseModal.show: failed to show modal', error);
        return Promise.reject(error);
    }
}

/**
 * Validate modal presence
 */
function validateModalPresence(id) {
    if (!document.getElementById(id)) {
        console.warn(
            `BaseModal: ⚠️Seems like you have missed %c'${id}'`,
            'background-color: #f8f9fa;color: #50596c;font-weight: bold;',
            'ID somewhere in your code. Refer example below to resolve it.'
        );
        console.warn(
            '%cExample:',
            'background-color: #f8f9fa;color: #50596c;font-weight: bold;',
            `<div class="bm-modal" id="${id}"></div>`
        );
        return false;
    }
    return true;
}

/**
 * Validate trigger presence
 */
function validateTriggerPresence(triggers) {
    if (triggers.length <= 0) {
        console.warn(
            'BaseModal: ⚠️Please specify at least one %c\'data-basemodal-trigger\'',
            'background-color: #f8f9fa;color: #50596c;font-weight: bold;',
            'data attribute.'
        );
        console.warn(
            '%cExample:',
            'background-color: #f8f9fa;color: #50596c;font-weight: bold;',
            '<a href="#" data-basemodal-trigger="my-modal"></a>'
        );
        return false;
    }
    return true;
}

/**
 * Validate arguments
 */
function validateArgs(triggers, triggerMap) {
    validateTriggerPresence(triggers);
    if (!triggerMap) return true;
    for (const id in triggerMap) {
        validateModalPresence(id);
    }
    return true;
}

/**
 * Initialize BaseModal
 */
export function init(userConfig) {
    // Merge user config with defaults and save globally
    Object.assign(config, userConfig || {});

    const options = {openTrigger:'data-basemodal-trigger', ...config};

    // Collects all the nodes with the trigger
    const triggers = [...document.querySelectorAll(`[${options.openTrigger}]`)];

    // Makes a mappings of modals with their trigger nodes
    const triggerMap = generateTriggerMap(triggers, options.openTrigger);

    // Checks if modals and triggers exist in dom
    if (options.debugMode === true && validateArgs(triggers, triggerMap) === false) return;

    // For every target modal creates a new instance
    for (const key in triggerMap) {
        const value         = triggerMap[key];
        options.targetModal = key;
        options.triggers    = [...value];
        activeModal         = new Modal(options);
        activeModals[key]   = activeModal;
    }
}

/**
 * Register and initialize a plugin
 * @param {Function} PluginClass - Plugin class extending BasePlugin
 * @param {Object} config - Plugin configuration options
 * @returns {Object} Plugin instance
 * @public
 */
export function use(PluginClass, config = {}) {
    if (!PluginClass || typeof PluginClass !== 'function') {
        console.error('BaseModal.use: PluginClass must be a valid constructor');
        return null;
    }

    const plugin = new PluginClass(config);
    plugin.init(window.BaseModal);

    registeredPlugins[plugin.name] = plugin;

    if (config.debugMode) {
        console.log(`BaseModal: Plugin "${plugin.name}" registered`);
    }

    return plugin;
}

/**
 * Get registered plugin by name
 * @param {string} name - Plugin name (e.g., 'BaseMedia', 'BaseInline')
 * @returns {Object|null} Plugin instance or null if not found
 * @public
 */
export function getPlugin(name) {
    return registeredPlugins[name] || null;
}

/**
 * Get all registered plugins
 * @returns {Object} Object with all registered plugins
 * @public
 */
export function getPlugins() {
    return {...registeredPlugins};
}


/**
 * Close modal
 */
export function close(targetModal) {
    // Check if targetModal is provided
    if (targetModal && activeModals[targetModal]) {
        // Close the modal by its id
        return activeModals[targetModal].closeModal();
    } else if (activeModal) {
        // Close the last active modal
        return activeModal.closeModal();
    }
    return Promise.resolve();
}

/**
 * Destroy modal instance
 */
export function destroy(targetModal) {
    if (targetModal && activeModals[targetModal]) {
        activeModals[targetModal].destroy();
        delete activeModals[targetModal];
    } else if (!targetModal) {
        // Destroy all modals
        Object.values(activeModals).forEach(modal => {
            if (modal && modal.destroy) {
                modal.destroy();
            }
        });
        Object.keys(activeModals).forEach(key => delete activeModals[key]);
        activeModal       = null;
        modalStack.length = 0;
    }
}

/**
 * Show confirmation dialog
 */
export function confirm(message, options = {}) {
    return new Promise((resolve) => {
        const modalId    = `modal-confirm-${Date.now()}`;
        const closeAttr  = config.closeTrigger || 'data-basemodal-close';
        const {
                  title        = '',
                  confirmText  = 'Да',
                  cancelText   = 'Отмена',
                  confirmClass = 'b-btn--primary',
                  cancelClass  = 'b-btn--secondary'
              }       = options;

        // Create modal HTML
        const modalHTML = `
            <div id="${modalId}" class="bm-modal" aria-hidden="true">
                <div class="bm-modal__overlay" ${closeAttr}>
                    <div class="bm-modal__container bm-modal__container--small" role="dialog" aria-modal="true">
                        <div class="bm-modal__content">
                            ${title ? `<h2 class="bm-modal__title">${title}</h2>` : ''}
                            <p class="bm-modal__message">${message}</p>
                            <div class="bm-modal__buttons">
                                <button class="b-btn ${confirmClass}" data-confirm="yes">
                                    ${confirmText}
                                </button>
                                <button class="b-btn ${cancelClass}" data-confirm="no">
                                    ${cancelText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bm-modal__bg" aria-hidden="true" ${closeAttr}></div>
            </div>
        `;

        // Insert modal into DOM
        document.body.insertAdjacentHTML('beforeend', modalHTML);
        const modal = document.getElementById(modalId);

        // Cleanup function
        const cleanup = async () => {
            await close(modalId);
            // Wait for close animation
            setTimeout(() => {
                if (modal && modal.parentNode) {
                    modal.remove();
                }
                if (activeModals[modalId]) {
                    delete activeModals[modalId];
                }
            }, 300);
        };

        // Handle confirm
        modal.querySelector('[data-confirm="yes"]').onclick = async () => {
            await cleanup();
            resolve(true);
        };

        // Handle cancel
        modal.querySelector('[data-confirm="no"]').onclick = async () => {
            await cleanup();
            resolve(false);
        };

        // Handle close via overlay/bg
        modal.querySelectorAll(`[${closeAttr}]`).forEach(el => {
            el.onclick = async (e) => {
                if (e.target === el) {
                    await cleanup();
                    resolve(false);
                }
            };
        });

        // Show modal
        show(modalId, {
            awaitOpenAnimation :true,
            awaitCloseAnimation:true
        }).catch(console.error);
    });
}

/**
 * Check if modal is open
 */
export function isOpen(targetModal) {
    const modal = document.getElementById(targetModal);
    return modal && modal.classList.contains('is-open');
}

/**
 * Get all open modal IDs
 */
export function getOpenModals() {
    return modalStack.map(instance => instance.modal.id);
}

/**
 * Close all modals
 */
export async function closeAll() {
    const promises = [];
    // Close in reverse order (from top to bottom)
    while (modalStack.length > 0) {
        const instance = modalStack[modalStack.length - 1];
        promises.push(instance.closeModal());
    }
    return Promise.all(promises);
}

/**
 * Close topmost modal
 */
export function closeTopmost() {
    if (modalStack.length > 0) {
        const topModal = modalStack[modalStack.length - 1];
        return topModal.closeModal();
    }
    return Promise.resolve();
}

/**
 * Get modal instance
 */
export function getInstance(targetModal) {
    return activeModals[targetModal] || null;
}

/**
 * Set default configuration
 */
export function setDefaults(defaults) {
    if (typeof defaults !== 'object') {
        console.error('BaseModal.setDefaults: defaults must be an object');
        return;
    }
    Object.assign(config, defaults);
}

/**
 * Destroy all modals
 */
export function destroyAll() {
    Object.keys(activeModals).forEach(id => {
        if (activeModals[id]) {
            activeModals[id].destroy();
            delete activeModals[id];
        }
    });
    modalStack.length = 0;
    activeModal       = null;
}

/**
 * Set theme for all modals
 */
export function setTheme(theme) {
    const validThemes = ['light', 'dark', 'auto'];
    if (!validThemes.includes(theme)) {
        console.error(`BaseModal.setTheme: Invalid theme "${theme}". Use: ${validThemes.join(', ')}`);
        return;
    }

    // Update default theme
    config.theme = theme;

    // Apply to all open modals
    modalStack.forEach(instance => {
        // Remove existing theme classes
        instance.modal.classList.remove('bm-modal--theme-light', 'bm-modal--theme-dark');

        let themeToApply = theme;
        if (theme === 'auto') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            themeToApply      = prefersDark ? 'dark' : 'light';
        }

        if (themeToApply !== 'auto') {
            instance.modal.classList.add(`bm-modal--theme-${themeToApply}`);
        }
    });
}

export default {
    init,
    show,
    close,
    destroy,
    confirm,
    isOpen,
    getOpenModals,
    closeAll,
    closeTopmost,
    getInstance,
    setDefaults,
    destroyAll,
    setTheme,
    use,
    getPlugin,
    getPlugins
};
